// Branding-elements-as-real-elements — Phase 5.
//
// `BrandingElement` is the runtime class for an `Element` row whose
// `source` field is `"theme"`. It looks and behaves like
// `DynamicElement` for option reads/writes, sanitize-for-publish,
// and the inspector — what changes is the seam where the rest of
// the editor decides whether an element is part of the visible
// destination tree or part of the branding-inheritance backplane.
//
// Key behavioural deltas vs. the base class:
//
//   * `isTheme` returns true. Every visible-tree walker that should
//     skip theme elements gates on this (canvas render, layers
//     panel, drag-drop hit-testing, publish `contents` recursion).
//   * `brandedVariant` / `brandedSize` surface the entity's
//     branding-coordinate columns so the per-DC theme-element
//     index in `DynamicComponent.getThemeElementFor(...)` can key
//     on them without re-parsing the options tree.
//   * `childElementIds` always returns []. Theme elements do not
//     own children — they exist as branding presets, not as
//     containers. This guards every recursive walk that might still
//     reach a theme element via a stale ancestor lookup.
//
// `parentElementId` is left at the base getter (which reads the
// entity's `parentElement` field). The schema persists `null` for
// theme elements, so the getter returns undefined naturally — no
// override needed.
//
// `sanitize()` is also inherited: theme elements need their option
// trees compiled the same way destination elements do at publish
// time (see Phase 7 — the publish flow embeds each theme element's
// sanitized output into the release's `themeElements` map).

import DynamicElement from "%/dynamic-component-editor/classes/dynamic-element";
import BaseBranding from "%/dynamic-component-editor/classes/branding/base-branding";

const EMPTY_OVERRIDDEN_SET = new Set();

class BrandingElement extends DynamicElement {
  get isTheme() {
    return true;
  }

  get isDestination() {
    return false;
  }

  get brandedVariant() {
    return this.element?.brandedVariant ?? null;
  }

  get brandedSize() {
    return this.element?.brandedSize ?? null;
  }

  // Base coordinates (Heading/Standard, Button/Primary/Standard) are
  // the editable root of their variant chain — there's nothing for
  // them to override. The persisted set on disk still lists every
  // non-`i:true` leaf at default/default (collectOverriddenOptionKeys
  // includes default/default for theme rows so writes for Accent/Subtle
  // are tracked), but reading it back through the inspector should
  // pretend the set is empty here. Otherwise the V2 inspector's state
  // tabs AND each tool's ConfigOptionGroupHeader stamp the warning
  // triangle on every option the user touches at Primary/Standard.
  // `BaseBranding.isInspectorBaseThemeElement` is the canonical
  // inspector-only gate documented for this exact case.
  get overriddenOptionKeysSet() {
    if (BaseBranding.isInspectorBaseThemeElement(this.brandedVariant, this.brandedSize)) {
      return EMPTY_OVERRIDDEN_SET;
    }
    return super.overriddenOptionKeysSet;
  }

  // Theme elements never contain children — the branding-inheritance
  // backplane is flat by design, and the DC's `themeElements` feed
  // lists every theme element directly, no nesting. Returning an
  // empty list here defends recursive walks (e.g. `sanitize()`'s
  // children sweep, layers-panel descendants) from accidentally
  // diving into a theme element via cached state.
  get childElementIds() {
    return [];
  }

  // Inspector tools whose `optionKey` matches one of these strings
  // are stripped from the branding inspector. Each is a self-
  // rendered tool whose UI lets a canvas instance change its
  // identity (which BrandingElement to inherit from) — for the
  // BrandingElement entity itself, the (type, variant, size)
  // coordinate IS the identity, so exposing those pickers would
  // let users corrupt the branding-map key.
  //
  //   * `styled-text` — the BrandedTextOptions picker on
  //     typography elements (heading/paragraph/… + standard/accent
  //     /subtle).
  //   * `button` — the ButtonOptions picker on ButtonElement
  //     (variant + size + label + action). The label and the
  //     icon/icon-position controls live inside the same self-
  //     rendered toolset; if surfaced separately later they need
  //     their own bundles, but for now hiding the whole picker
  //     keeps the branding inspector focused on style controls
  //     (background, border, padding, …).
  static #BRANDING_INSPECTOR_HIDDEN_OPTION_KEYS = new Set([
    "styled-text",
    "button",
  ]);

  getOptionTools(context) {
    const tools = super.getOptionTools(context);
    return tools.filter(tool => !BrandingElement.#BRANDING_INSPECTOR_HIDDEN_OPTION_KEYS.has(tool?.optionKey));
  }

  // No `isOptionOverridden` / `determineIfOptionsAreInheriting` /
  // `optionCanBeInherited` overrides here — the branding-aware
  // logic in `DynamicOption` already gates on
  // `BaseBranding.isBaseThemeElement` for theme rows:
  //
  //   * Base coord (Heading/Standard, Button/Primary/Standard) at
  //     default/default → never overridden, never inheriting,
  //     can't be inherited from. The leaf IS the source of truth.
  //   * Non-base coord (Heading/Accent, Button/Secondary/*,
  //     Button/Tertiary/*) → inheriting unless the user has
  //     explicitly overridden the option (leaf without `i: true`).
  //     The inspector renders the override affordance only on
  //     non-base coords, which matches the user's mental model
  //     (Accent inherits from Standard; Standard is the source).
  //
  // For that branching to fire correctly, `#elementDataForOption`
  // on the base class injects `elementType: "theme"` plus the
  // entity's `brandedVariant`/`brandedSize`/`brandedType` into
  // every elementData payload threaded through `DynamicOption`.
}

export default BrandingElement;
